import React, { useState, useEffect, useRef, ReactElement, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Container from '../../components/blocks/Container';
import styled from '@emotion/styled';
import OrderSummary from '../../atoms/billing/OrderSummary';
import CreditCardPanel from '../../atoms/billing/CreditCardPanel';
import { browserHistory, getLocalItem, toaster } from '../../common';
import { getTextColor, getTextType, getBackgroundTheme, getPaymentTheme } from '../../style';
import every from 'lodash/every';
import values from 'lodash/values';
import omit from 'lodash/omit';
import CreditCardSwitcher from '../../atoms/billing/CreditCardSwitcher';
import Button from '../../atoms/buttons/Button';
import BillingHeader from '../../components/blocks/headerNav/BillingHeader';
import find from 'lodash/find';
import { getCookie, removeCookie, renderTaxLabel, tabletWidth } from '../../utils/helpers';
import LockIcon from '@material-ui/icons/Lock';
import AfterPayment from '../../modules/billing/AfterPayment';
import { getIDVPackages, promoCodeCheck } from '../../sdk';
import BillingPaymentProcessor from '../../atoms/billing/BillingPaymentProcessor';
import PaymentDeclined from '../../atoms/billing/PaymentDeclined';
import { countryWithTaxByProvince, s3BucketContentDir } from '../../config/Master';
import useMixPanel from '../../hooks/useMixPanel';
import { EVENT_NAMES } from '../../constants/mixpanel';
import { handlePaymentVysionEvent } from '../../utils/vysion/payment';
import { VYSION_ACTIONS } from '../../utils/vysion';
import { PaymentClickVysionEventsEnum } from '../../utils/vysion/payment/click';
import BillingForm from '../../modules/billing/BillingForm';
import TrackLoadingToMixpanel from '../../components/common/TrackLoadingToMixPanel';
import { PROMO_COOKIE } from '../../config/constants';
import SofortForm from '../../modules/billing/SofortForm';
import BillingTermsAndCondition from '../../modules/billing/BillingTermsAndCondition';
import BillingLoading from './BillingLoading/BillingLoading';
import Spinner from '../../atoms/loader/Spinner/Spinner';
import GooglePayForm from '../../atoms/billing/GooglePay/GooglePayForm';
import { GPAY_PAYMENT_CHANNEL } from '../../config/gpay';
import './BillingPaymentStyles.css';
import ApplePayForm from '../../modules/ApplePay/ApplePayForm';
import { APAY_PAYMENT_CHANNEL } from '../../config/apay';
import { isApplePayEligible } from '../../utils/billing';
import { IPackageData } from '../../atoms/billing/types/OrderSummary';
import { IBillingPaymentProps } from './types/BillingPayment';
import useMediaBreakpoints from '../../hooks/useMediaBreakpoints';
import breakpoints from '../../theme/material/breakpoints';

const FormPanel = styled('div')({
  maxWidth: 708,
  margin: '0 auto',
  borderRadius: 10,
  marginBottom: 60,
  [breakpoints.down(640.05)]: {
    backgroundColor: 'transparent',
    border: 0,
  },
});

const BillingFormContainer = styled('div')(() => ({
  padding: '30px 72px',
  maxWidth: 708,
  margin: '0 auto',
  border: '1px solid #24A83A',
  background: getBackgroundTheme().paymentBlock,
  borderRadius: 10,
  [breakpoints.down(600.05)]: {
    padding: '20px 10px 20px 10px',
  },
}));

const BillingFormHeader = styled('div')(() => ({
  ...getTextColor().DARKER,
  fontSize: 16,
  fontWeight: 600,
  marginBottom: 20,
  alignItems: 'center',
  display: 'flex',
  flexWrap: 'wrap',
  width: 564,
  [breakpoints.down(835.05)]: {
    width: 'auto',
    marginBottom: 30,
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

const ProcessingHeader = styled('h3')(() => ({
  ...getTextType().processingHeader,
}));

const PackageSection = styled('div')(() => ({
  maxWidth: 708,
  margin: '0 auto',
  borderRadius: 10,
  border: `1px solid ${getPaymentTheme().SUMMARY.borderColor}`,
  padding: 20,
  background: getBackgroundTheme().orderSummary,
  marginBottom: 20,
  [breakpoints.down(769.05)]: {
    padding: 10,
    paddingTop: 20,
  },
}));

const PaymentCheck = styled('img')({
  float: 'left',
});

const PaymentSecureText = styled('span')({
  fontSize: 18,
  fontWeight: 700,
  letterSpacing: '-0.18px',
  float: 'left',
});

const PaymentCheckMobile = styled(PaymentCheck)({
  [breakpoints.down(835.05)]: {
    marginLeft: 'auto',
    float: 'none',
  },
});

const PaymentSecureTextMobile = styled(PaymentSecureText)({
  [breakpoints.down(835.05)]: {
    marginRight: 'auto',
    float: 'none',
  },
});

const CreditCardPanelMobile = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  flexBasis: '100%',
});

// @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
const PayWithSavedCardButton = styled(Button)(() => ({
  margin: '0 auto',
  padding: '16px 32px !important',
  display: 'flex !important',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '18px !important',
  height: 51,
  width: 232,
  borderRadius: '6px !important',
}));

const BillingPayment = ({
  savedCard,
  errors,
  defaultCountry,
  profile,
  processingPayment,
  statuses,
  descriptors,
  handleSubmit,
  handleDeleteSavedCard,
  packages,
  calculateChargeAmount,
  alternativePayment,
  rocketgateGooglepay,
  defaultState,
  showAutoTopup,
  setIsLoading,
  isLoading = false,
  applepayCredentials,
}: IBillingPaymentProps): ReactElement => {
  const profileData = profile?.profile?.data;
  const [recurring, setRecurring] = useState(true);
  const [allowTokenSubmit, setAllowTokenSubmit] = useState(true);
  const [use2PayData, setUse2PayData] = useState({
    use2pay_package_id: '',
    use2pay_billing_name_on_card: '',
    use2pay_card_number: '',
    use2pay_card_cvv: '',
    use2pay_billing_address: '',
    use2pay_billing_city: '',
    use2pay_billing_state: defaultState,
    use2pay_billing_zip: '',
    use2pay_billing_country: defaultCountry,
    use2pay_card_exp_month: null,
    use2pay_card_exp_year: null,
    promo_code: null,
    save_card: true,
    new_card: true,
    selectedPackage: null,
    payment_channel: null,
    showForm: false,
    showDeclinedPayment: false,
    recurring: false,
    showCvv2: false,
  });
  const { isMobile } = useMediaBreakpoints();

  const [discountAmount, setDiscountAmount] = useState(0);
  const [discount, setDiscount] = useState({
    discount: 0,
    type: null,
    code: null,
  });
  const [selectedPackageData, setSelectedPackageData] = useState<IPackageData>({});
  const mounted = useRef();
  const pckgRef = useRef();

  const { setMixpanelDataByKeyHandler } = useMixPanel();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const setSelectedPackage = useCallback(async () => {
    // const queryStringParsed = queryString.parse(location.search);

    const promoCode = searchParams.get('promoCode') || null;
    const packageId = searchParams.get('selectedPackageId') || null;
    const isFromIDVPackage = searchParams.get('isFromIDVPackage') === 'true' || null;

    if (!packageId) {
      navigate('/packages');
      return;
    }

    const storedPackages = getLocalItem('wyp_billing_packages');
    let pckgs = storedPackages ? JSON.parse(storedPackages) : packages;
    const selectedPackageId = searchParams.get('selectedPackageId');

    const packageIDVExists = find(pckgs, { identifier: selectedPackageId });

    if (
      (selectedPackageId === 'idv_last_minute' && !isFromIDVPackage) ||
      (selectedPackageId === 'idv_last_minute' && !packageIDVExists && isFromIDVPackage)
    ) {
      const idvPackagesRes = await getIDVPackages();
      const idvPackages = idvPackagesRes.data.data;

      if (idvPackages) {
        pckgs = idvPackages.concat(pckgs);
      }
    }

    if (!pckgs) {
      navigate('/packages');
      return;
    }

    const selectedPackage = find(pckgs, { identifier: selectedPackageId });

    if (!selectedPackage) {
      navigate('/packages');
      toaster.warn('Promo package is no longer valid.');
      return;
    }

    const newData = {
      selectedPackage,
      use2pay_package_id: selectedPackageId || null,
      showForm: true,
      promo_code: promoCode,
      new_card: typeof savedCard === 'undefined',
      recurring: !isFromIDVPackage,
    };

    const newUse2PayData = {
      ...use2PayData,
      ...newData,
    };

    setUse2PayData(newUse2PayData);
    setSelectedPackageData(selectedPackage);
    // Checks promoCode and packageId
    if (promoCode && packageId) {
      const promoCodeRef = getCookie(PROMO_COOKIE);
      const checkRes = await promoCodeCheck(
        profile.profile.data.hash_id,
        promoCode,
        packageId,
        promoCodeRef,
        use2PayData.use2pay_billing_country,
        true
      );

      if (!checkRes.data) {
        navigate('/packages');
        toaster.warn('Promo package is no longer valid.');

        if (promoCodeRef && promoCodeRef === promoCode) {
          removeCookie(PROMO_COOKIE);
        }

        return;
      }

      if (checkRes) {
        let discountAmount = 0;

        if (checkRes.data.discount_rate) {
          const discountRate = checkRes.data.discount_rate;

          discountAmount = selectedPackage.price * discountRate;
        }

        if (checkRes.data.discount_amount) {
          discountAmount = checkRes.data.discount_amount;
        }

        if (checkRes.data.package) {
          setSelectedPackageData(checkRes.data.package.data);
        }

        setDiscount(checkRes.data);
        setDiscountAmount(discountAmount);
      }
    }
  }, [searchParams, packages, profile?.profile?.data?.hash_id, savedCard, use2PayData, setSelectedPackageData]);

  useEffect(() => {
    if (!mounted.current) {
      // do componentDidMount logic
      setSelectedPackage();
      // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
      mounted.current = true;
    } else {
      // do componentDidUpdate logic
      if (!use2PayData.showForm && errors && typeof errors.data !== 'undefined') {
        const newData = {
          showForm: true,
          use2pay_card_number: '',
          use2pay_card_cvv: '',
          use2pay_card_exp_month: null,
          use2pay_card_exp_year: null,
          showDeclinedPayment: true,
          showCvv2: errors.data.error_code === 'BILL_CVV2_REQUIRED',
        };

        setAllowTokenSubmit(errors.data.error_code !== 'BILL_CVV2_REQUIRED');

        setUse2PayData({
          ...use2PayData,
          ...newData,
        });
      }
    }
  }, [errors, use2PayData, setSelectedPackage]);

  useEffect(() => {
    if (use2PayData.showForm && !pckgRef.current && defaultCountry === use2PayData.use2pay_billing_country) {
      // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
      pckgRef.current = true;
      handleCalculateChargeVat(use2PayData.use2pay_billing_country, use2PayData.selectedPackage);
    }

    browserHistory.listen((loc, action) => {
      if (action === 'POP' && selectedPackageData && selectedPackageData.identifier === 'idv_last_minute') {
        navigate('/dashboard');
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPackageData]);

  useEffect(() => {
    if (defaultCountry !== use2PayData.use2pay_billing_country) {
      setTimeout(() => {
        const newUse2PayData = {
          ...use2PayData,
          ...{ use2pay_billing_country: defaultCountry },
        };

        setUse2PayData(newUse2PayData);
        handleCalculateChargeVat(defaultCountry, newUse2PayData.selectedPackage, {}, defaultState);
      }, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultCountry]);

  const handleCalculateChargeVat = useCallback(
    async (country, packageData, newCard = {}, state = null, paymentChannel = null) => {
      const newTaxLocation = packageData && typeof packageData.taxes !== 'undefined' ? packageData.taxes : null;
      const currentTaxLocation =
        selectedPackageData && typeof selectedPackageData.taxes !== 'undefined' ? selectedPackageData.taxes : null;
      const taxCountryPayload = haveTaxByProvince(country) && state ? `${country}-${state}` : country;

      if (
        newTaxLocation &&
        currentTaxLocation &&
        taxCountryPayload === currentTaxLocation?.location &&
        ![GPAY_PAYMENT_CHANNEL, APAY_PAYMENT_CHANNEL].includes(paymentChannel)
      ) {
        return;
      }

      if (use2PayData.showForm && packageData.identifier) {
        setIsLoading(true);
        calculateChargeAmount(taxCountryPayload, packageData.identifier, use2PayData.promo_code, paymentChannel)
          .then(response => {
            const chargeVatPackage = response.data.data || {};

            if (chargeVatPackage.identifier) {
              setSelectedPackageData(chargeVatPackage);
              setUse2PayData({
                ...use2PayData,
                ...{
                  selectedPackage: chargeVatPackage,
                  use2pay_billing_country: country,
                  use2pay_billing_state: state == '' || state !== null ? state : use2PayData.use2pay_billing_state,
                  use2pay_billing_city:
                    country !== use2PayData.use2pay_billing_country ? '' : use2PayData.use2pay_billing_city,
                  use2pay_billing_zip:
                    country !== use2PayData.use2pay_billing_country ? '' : use2PayData.use2pay_billing_zip,
                  use2pay_billing_address:
                    country !== use2PayData.use2pay_billing_country ? '' : use2PayData.use2pay_billing_address,
                },
                ...newCard,
              });
            }

            setIsLoading(false);
          })
          .catch(() => {
            setIsLoading(false);
          });
      } else {
        const selectedPackageCopy = use2PayData && use2PayData.selectedPackage;

        if (selectedPackageCopy) {
          if (selectedPackageCopy.taxes) {
            delete selectedPackageCopy.taxes;
          }

          setUse2PayData({
            ...use2PayData,
            ...{
              selectedPackage: selectedPackageCopy,
              use2pay_billing_country: country,
              use2pay_billing_state: state == '' || state !== null ? state : use2PayData.use2pay_billing_state,
              use2pay_billing_city:
                country !== use2PayData.use2pay_billing_country ? '' : use2PayData.use2pay_billing_city,
              use2pay_billing_zip:
                country !== use2PayData.use2pay_billing_country ? '' : use2PayData.use2pay_billing_zip,
              use2pay_billing_address:
                country !== use2PayData.use2pay_billing_country ? '' : use2PayData.use2pay_billing_address,
            },
            ...newCard,
          });
          setSelectedPackageData(selectedPackageCopy);
        }
        setIsLoading(false);
      }
    },
    [calculateChargeAmount, use2PayData, setSelectedPackageData, selectedPackageData]
  );

  const handlePostalCodeChange = newAddress => {
    if (haveTaxByProvince(use2PayData.use2pay_billing_country)) {
      handleCalculateChargeVat(
        use2PayData.use2pay_billing_country,
        use2PayData.selectedPackage,
        {},
        newAddress.use2pay_billing_state
      );
    }
    setUse2PayData({
      ...use2PayData,
      ...newAddress,
    });
  };

  const handleTextFieldChange = (id: number, value: string | number) => {
    const textInput = { [`use2pay_${id}`]: value };

    setUse2PayData({
      ...use2PayData,
      ...textInput,
    });
  };

  const handleTokenCvvInputChange = (event, cardType): any => {
    const amexType = ['american express', 'amex'];
    const maxChar = amexType.includes(cardType?.toLowerCase()) ? 4 : 3;
    const value = ((event?.target?.value || '').match(/\d+/g) || []).join('');
    const charLEngth = value.length || 0;

    event.preventDefault();

    if (charLEngth > maxChar) {
      return;
    }

    const textInput = {
      use2pay_token_cvv: value,
      showDeclinedPayment: charLEngth <= 0,
    };

    setUse2PayData({
      ...use2PayData,
      ...textInput,
    });
    setAllowTokenSubmit(charLEngth === maxChar);
  };

  const handleUpdateMonthYrInput = (mth, yr) => {
    const cardMthYr = {
      use2pay_card_exp_month: mth,
      use2pay_card_exp_year: yr,
    };

    setUse2PayData({
      ...use2PayData,
      ...cardMthYr,
    });
  };

  const handleCountryChange = value => {
    const billingCountry = { use2pay_billing_country: value, showForm: true };

    const refreshValue = {
      use2pay_billing_address: '',
      use2pay_billing_city: '',
      use2pay_billing_state: '',
      use2pay_billing_zip: '',
    };

    const newUse2PayData = {
      ...use2PayData,
      ...billingCountry,
      ...refreshValue,
    };

    setUse2PayData(newUse2PayData);

    if (haveTaxByProvince(value) && use2PayData.use2pay_billing_state.length > 0) {
      const updatedState = value !== use2PayData.use2pay_billing_country ? '' : use2PayData.use2pay_billing_state;

      handleCalculateChargeVat(value, use2PayData.selectedPackage, {}, updatedState);
    } else {
      handleCalculateChargeVat(value, newUse2PayData.selectedPackage, {}, '');
    }
  };

  const handleSelectSaveCard = () => {
    if (use2PayData.recurring) return;

    const saveCard = { save_card: !use2PayData.save_card };

    setUse2PayData({
      ...use2PayData,
      ...saveCard,
    });
  };

  const handleChangePaymentOption = (isNewCard: boolean, paymentChannel?: string | null) => {
    const newCard = {
      new_card: isNewCard,
      payment_channel: paymentChannel,
      use2pay_payment_channel: paymentChannel,
      use2pay_billing_state: isNewCard ? '' : defaultState,
      showForm: true,
    };

    if (paymentChannel === null) {
      delete newCard.use2pay_payment_channel;
      // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
      delete use2PayData.use2pay_payment_channel;
    }

    if (
      isNewCard &&
      haveTaxByProvince(use2PayData.use2pay_billing_country) &&
      use2PayData.use2pay_billing_state !== '' &&
      use2PayData.selectedPackage &&
      use2PayData.selectedPackage.taxes
    ) {
      delete use2PayData.selectedPackage.taxes;
    }

    setUse2PayData({
      ...use2PayData,
      ...newCard,
    });

    if (paymentChannel === null && !isNewCard && savedCard && typeof savedCard.country !== 'undefined') {
      handleCalculateChargeVat(
        savedCard.country,
        use2PayData.selectedPackage,
        newCard,
        newCard.use2pay_billing_state,
        paymentChannel
      );
    }

    if (paymentChannel !== null || (paymentChannel === null && use2PayData.payment_channel !== null)) {
      handleCalculateChargeVat(
        use2PayData.use2pay_billing_country,
        use2PayData.selectedPackage,
        newCard,
        newCard.use2pay_billing_state,
        paymentChannel
      );
    }
  };

  const handleShowPackages = () => {
    if (selectedPackageData && selectedPackageData.identifier === 'idv_last_minute') {
      navigate('/dashboard', {
        replace: true,
      });
    } else {
      navigate(-1);
    }
  };

  const hidePackageForm = () => {
    const showForm = { showForm: false };

    setUse2PayData({
      ...use2PayData,
      ...showForm,
    });
  };

  const applyPromoCode = code => {
    const newUse2PayData = { promo_code: code };

    setUse2PayData({
      ...use2PayData,
      ...newUse2PayData,
    });
  };

  const haveTaxByProvince = country => countryWithTaxByProvince.split(',').includes(country);

  if ((processingPayment || !use2PayData.showForm) && (statuses && Object.keys(statuses).length) <= 0) {
    return (
      <div
        data-test-id="cy-processing-payment"
        style={{
          textAlign: 'center',
          margin: `${isMobile ? '50%' : '180px'} auto 0`,
          maxWidth: 350,
        }}
      >
        <Spinner />
        <TrackLoadingToMixpanel page="Payment" />
        <ProcessingHeader>Processing payment...</ProcessingHeader>
        <p>
          Please wait a moment as we authorize your payment. You may be asked for additonal verification from your card
          issuer. If so, please return after providing it.
        </p>
      </div>
    );
  }

  const handleAutoTopupChange = () => {
    const newRecurringtState = !recurring;

    handlePaymentVysionEvent({
      action: VYSION_ACTIONS.CLICK,
      event: newRecurringtState
        ? PaymentClickVysionEventsEnum.CLICK_PAYMENT_AUTO_CREDIT_TOP_UP_ON
        : PaymentClickVysionEventsEnum.CLICK_PAYMENT_AUTO_CREDIT_TOP_UP_OFF,
    });

    const recurringData = { recurring: newRecurringtState, save_card: true };

    setUse2PayData({
      ...use2PayData,
      ...recurringData,
    });
    setRecurring(newRecurringtState);
  };

  const isIDV = use2PayData && use2PayData.selectedPackage?.identifier === 'idv_last_minute';
  const renderSummaryHeader = () => {
    return isMobile && tabletWidth ? (
      <BillingFormHeader data-test-id="billing-form-header">
        <PaymentCheckMobile
          src={`${s3BucketContentDir}payment/payment_check.png`}
          width="32"
          height="32"
          alt="Secure Payment Details"
        />{' '}
        <PaymentSecureTextMobile>Secure Payment Details</PaymentSecureTextMobile>
        <CreditCardPanelMobile>
          <CreditCardPanel
            isApplePayEligible={isApplePayEligible(applepayCredentials)}
            rocketgateGooglepay={rocketgateGooglepay}
          />
        </CreditCardPanelMobile>
      </BillingFormHeader>
    ) : (
      <BillingFormHeader data-test-id="billing-form-header">
        <PaymentCheck
          src={`${s3BucketContentDir}payment/payment_check.png`}
          width="32"
          height="32"
          alt="Secure Payment Details"
        />{' '}
        <PaymentSecureText>Secure Payment Details</PaymentSecureText>{' '}
        <CreditCardPanel
          isApplePayEligible={isApplePayEligible(applepayCredentials)}
          rocketgateGooglepay={rocketgateGooglepay}
        />
      </BillingFormHeader>
    );
  };

  const getPaymentMethod = use2PayData => {
    return use2PayData.payment_channel === 'sofort' ? 'Sofort' : 'Credit Card';
  };

  const handleDeleteCard = tokenId => {
    setUse2PayData({
      ...use2PayData,
      ...{ showDeclinedPayment: false },
    });

    return handleDeleteSavedCard(tokenId);
  };

  const renderBillingForm = () => {
    if (use2PayData.payment_channel && use2PayData.payment_channel === GPAY_PAYMENT_CHANNEL) {
      return (
        <div style={{ marginTop: isIDV ? 20 : 0 }}>
          <GooglePayForm
            paymentChannel={GPAY_PAYMENT_CHANNEL}
            use2PayData={use2PayData}
            handleSubmit={handleSubmit}
            hidePackageForm={hidePackageForm}
            userCountry={profileData.country}
            discount={discountAmount}
            descriptors={descriptors}
          />
        </div>
      );
    }

    if (use2PayData.payment_channel && use2PayData.payment_channel === APAY_PAYMENT_CHANNEL) {
      return (
        <div style={{ marginTop: isIDV ? 20 : 0 }}>
          <ApplePayForm
            use2PayData={use2PayData}
            handleSubmit={handleSubmit}
            hidePackageForm={hidePackageForm}
            userCountry={profileData.country}
            discount={discountAmount}
            applepayCredentials={applepayCredentials}
            descriptors={descriptors}
          />
        </div>
      );
    }

    if (use2PayData.payment_channel && use2PayData.payment_channel === 'sofort') {
      return (
        <SofortForm
          // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
          billingFirstName={use2PayData.use2pay_billing_fname}
          // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
          billingLastName={use2PayData.use2pay_billing_lname}
          billingChannel={use2PayData.payment_channel}
          billingCountry={use2PayData.use2pay_billing_country}
          handleTextFieldChange={handleTextFieldChange}
          handleUpdateMonthYrInput={handleUpdateMonthYrInput}
          handleCountryChange={handleCountryChange}
          handleSubmit={() => {
            hidePackageForm();
            handleSubmit(
              omit(use2PayData, [
                'new_card',
                'use2pay_billing_address',
                'use2pay_billing_city',
                'use2pay_billing_name_on_card',
                'use2pay_billing_state',
                'use2pay_billing_zip',
                'use2pay_card_cvv',
                'use2pay_card_exp_month',
                'use2pay_card_exp_year',
                'use2pay_card_number',
              ])
            );
          }}
          totalAmount={use2PayData.selectedPackage.price}
          currencyCharCode={use2PayData.selectedPackage.currency.data.label_unicode}
          newCard={savedCard && use2PayData.new_card}
          selectedPackage={use2PayData.selectedPackage}
          descriptors={descriptors}
          userCountry={profileData.country}
          discountAmount={discountAmount}
          recurring={recurring}
        />
      );
    }

    if (savedCard && !use2PayData.new_card) {
      return (
        <div style={{ marginTop: isIDV ? 20 : 0 }}>
          <PayWithSavedCardButton
            buttonType="primaryRed"
            onClick={() => {
              hidePackageForm();

              const tokenPayData = {
                use2pay_package_id: use2PayData.selectedPackage?.identifier,
                use2pay_card_reference: savedCard.token,
                promoCode: use2PayData.promo_code,
                use2pay_billing_country: savedCard.country,
                use2pay_billing_state: savedCard.state,
                address_country: savedCard.country,
                // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
                use2pay_token_cvv: use2PayData.use2pay_token_cvv,
                recurring: recurring,
                paymentType: savedCard.payment_type,
              };

              if (tokenPayData.use2pay_billing_state === null || tokenPayData.use2pay_billing_state.length <= 0) {
                delete tokenPayData.use2pay_billing_state;
              }

              handleSubmit(tokenPayData);
              setMixpanelDataByKeyHandler({
                key: EVENT_NAMES.CREDITS_PURCHASED,
                payload: {
                  Amount: selectedPackageData.credits,
                  Total: `${renderTaxLabel(selectedPackageData, discountAmount)}`,
                  AutoTopup: recurring,
                  PaymentMethod: 'Token',
                  PaymentSaved: true,
                },
              });
            }}
            disabled={!allowTokenSubmit}
          >
            <LockIcon style={{ fontSize: 19, paddingRight: 3 }} />
            {` Pay ${String.fromCharCode(selectedPackageData.currency?.data.label_unicode)}`}
            {renderTaxLabel(selectedPackageData, discountAmount)}
          </PayWithSavedCardButton>
          <BillingTermsAndCondition userCountry={profileData.country} />
          <BillingPaymentProcessor userCountry={profileData.country} />
          <AfterPayment descriptors={descriptors} />
        </div>
      );
    }

    return (
      <div>
        <BillingForm
          nameOnCard={use2PayData.use2pay_billing_name_on_card}
          cardNumber={use2PayData.use2pay_card_number}
          cardCVV={use2PayData.use2pay_card_cvv}
          billingAddress={use2PayData.use2pay_billing_address}
          billingCity={use2PayData.use2pay_billing_city}
          billingState={use2PayData.use2pay_billing_state}
          billingZip={use2PayData.use2pay_billing_zip}
          billingCountry={use2PayData.use2pay_billing_country}
          cardExpMonth={use2PayData.use2pay_card_exp_month}
          cardExpYear={use2PayData.use2pay_card_exp_year}
          onInputChange={handleTextFieldChange}
          onAddressChange={handlePostalCodeChange}
          handleUpdateMonthYrInput={handleUpdateMonthYrInput}
          handleCountryChange={handleCountryChange}
          disablePurchase={!every(values(omit(use2PayData, ['save_card', 'new_card'])), v => Boolean(v))}
          handleSubmit={() => {
            hidePackageForm();
            handleSubmit(omit(use2PayData, ['new_card']));

            setMixpanelDataByKeyHandler({
              key: EVENT_NAMES.CREDITS_PURCHASED,
              payload: {
                Amount: selectedPackageData.credits,
                Total: `${renderTaxLabel(selectedPackageData, discountAmount)}`,
                AutoTopup: use2PayData.recurring,
                PaymentMethod: getPaymentMethod(use2PayData),
                PaymentSaved: use2PayData.save_card,
              },
            });
          }}
          status={statuses}
          onSelectSaveCard={handleSelectSaveCard}
          saveCard={use2PayData.save_card}
          totalAmount={selectedPackageData.price}
          currencyCharCode={selectedPackageData.currency?.data.label_unicode}
          newCard={savedCard && use2PayData.new_card}
          selectedPackage={selectedPackageData}
          descriptors={descriptors}
          userCountry={profileData.country}
          discountAmount={discountAmount}
          recurring={recurring}
          onAutoTopupChange={handleAutoTopupChange}
          // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
          packages={packages}
          isIDV={isIDV}
          hasRecurring={showAutoTopup}
        />
      </div>
    );
  };

  return (
    // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
    <Container size="MD">
      <BillingLoading isLoading={isLoading} />
      {isMobile ? (
        <BillingHeader newBilling label="Your Payment Details" onBackClick={() => handleShowPackages()} />
      ) : (
        <BillingHeader label="Your Payment Details" />
      )}
      {(use2PayData.showForm || statuses) && (
        <FormPanel data-test-id="payment-form-panel">
          {selectedPackageData && (
            <PackageSection>
              <OrderSummary
                price={selectedPackageData.price}
                // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
                credits={parseFloat(selectedPackageData.credits)}
                currencyCharCode={selectedPackageData.currency?.data?.label_unicode}
                currencyCode={selectedPackageData.currency?.data?.code}
                vatDetails={selectedPackageData.taxes}
                isIDV={isIDV}
                discountAmount={discountAmount}
                hasRecurring={showAutoTopup}
                recurring={recurring}
                onAutoTopupChange={handleAutoTopupChange}
                packages={packages}
                selectedPackageData={selectedPackageData}
                discountData={discount}
                // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
                profile={profile}
                setDiscount={setDiscount}
                setDiscountAmount={setDiscountAmount}
                applyPromoCode={applyPromoCode}
                taxCountry={use2PayData.use2pay_billing_country}
                paymentChannel={use2PayData.payment_channel}
                setSelectedPackageData={setSelectedPackageData}
              />
            </PackageSection>
          )}
          <BillingFormContainer data-test-id="payment-form-container">
            {/* If has saved card, show switcher */}
            {renderSummaryHeader()}
            {use2PayData.showDeclinedPayment && <PaymentDeclined showTips={!use2PayData.showCvv2} />}
            <CreditCardSwitcher
              alternativePayment={alternativePayment}
              // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
              savedCardId={savedCard ? savedCard.id : ''}
              savedCardToken={savedCard ? savedCard.token : ''}
              savedCardType={savedCard ? savedCard.card_type : ''}
              savedCardLastFour={savedCard ? savedCard.last_four : ''}
              savedCardPaymentType={savedCard ? savedCard.payment_type : ''}
              onRadioButtonChangeHandler={handleChangePaymentOption}
              newCard={savedCard ? use2PayData.new_card : true}
              handleDeleteCard={handleDeleteCard}
              paymentChannel={use2PayData.payment_channel}
              use2PayData={use2PayData}
              handleSubmit={handleSubmit}
              handleSelectSaveCard={handleSelectSaveCard}
              discountAmount={discountAmount}
              statuses={statuses}
              // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
              savedCard={savedCard}
              rocketgateGooglepay={rocketgateGooglepay}
              onTokenCvvInputChange={handleTokenCvvInputChange}
              showCvv2={use2PayData.showCvv2}
              allowTokenSubmit={allowTokenSubmit}
              isApplePayEligible={isApplePayEligible(applepayCredentials)}
            />
            {renderBillingForm()}
          </BillingFormContainer>
        </FormPanel>
      )}
    </Container>
  );
};

export default BillingPayment;
